"use client";

import { useMemo, useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";

/* ------------------------------------------------------------------ */
/*  Regex constants (module level per Biome rule)                     */
/* ------------------------------------------------------------------ */

const RE_SENTENCE_SPLIT = /(?<=[.!?])\s+/;
// The word after "be" is a lookahead so "was being tested" is still found.
const RE_BE_PLUS_WORD =
  /\b(?:am|is|are|was|were|be|been|being)(?=(\s+)(\w+)\b)/gi;
const RE_SPLIT_WORDS = /\s+/;
const RE_REGULAR_PARTICIPLE = /^[a-z]{2,}ed$/i;
// "Fig. 1", "Eq. (3)", "Fig. S2": a figure-style abbreviation before a number.
const RE_REFERENCE_ABBREVIATION =
  /(?:^|[\s([])(?:figs?|eqs?|tab|tbl|tabs|nos?|vols?|pp?|ch|chs|sec|secs|sect|refs?|cf|app|ex|exh)\.$/i;
const RE_REFERENCE_NUMBER = /^[([]?(?:\d|[A-Z]\d)/;
// Titles and Latin abbreviations that never end a sentence.
const RE_NEVER_ENDS_SENTENCE =
  /(?:^|[\s([])(?:dr|mr|mrs|ms|prof|st|jr|sr|vs|al|inc|ltd|approx|ca|e\.g|i\.e)\.$/i;

// Irregular participles (no -ed ending) accepted after a form of "to be".
const IRREGULAR_PARTICIPLES = new Set(
  (
    "arisen awoken beaten begun bent bitten bled blown born borne bought bred broken brought built burnt " +
    "caught chosen cut dealt done drawn driven dug eaten fallen fed felt fled flown forbidden forgiven forgotten " +
    "found frozen given grown hanged heard held hidden hit hung hurt kept known laid lain led left lent " +
    "let lit lost made meant met paid proven put read ridden risen run said seen sent set sewn shaken shed shot shown " +
    "shut slept slid sold sought sped spent split spoken spread stolen stood struck stuck sung sunk sworn swept " +
    "swum taken taught thought thrown told torn trodden understood undone won woken woven worn wound written"
  ).split(" ")
);
// Compound participles such as "overtaken", "rewritten", "withdrawn".
const COMPOUND_SUFFIXES = [
  "taken",
  "given",
  "written",
  "broken",
  "chosen",
  "driven",
  "eaten",
  "fallen",
  "hidden",
  "shaken",
  "spoken",
  "stolen",
  "risen",
  "woven",
  "frozen",
  "beaten",
  "bitten",
  "forgotten",
  "drawn",
  "thrown",
  "shown",
  "grown",
  "known",
  "flown",
  "blown",
  "sworn",
  "torn",
  "proven",
];
// Words ending in -ed that are adjectives or nouns, not participles.
const NOT_PARTICIPLES = new Set([
  "need",
  "seed",
  "feed",
  "deed",
  "weed",
  "reed",
  "heed",
  "speed",
  "indeed",
  "hundred",
  "unknown",
  "sacred",
  "naked",
  "wicked",
  "kindred",
  "rugged",
  "ragged",
  "crooked",
  "beloved",
  "hatred",
  "bed",
  "red",
  "wed",
  "ted",
  "ed",
]);

/** Past-participle test: regular -ed words plus known irregular forms. */
export function isParticiple(word: string): boolean {
  const lower = word.toLowerCase();
  if (NOT_PARTICIPLES.has(lower)) {
    return false;
  }
  if (IRREGULAR_PARTICIPLES.has(lower)) {
    return true;
  }
  if (RE_REGULAR_PARTICIPLE.test(lower)) {
    return true;
  }
  return COMPOUND_SUFFIXES.some(
    (suffix) => lower.length > suffix.length && lower.endsWith(suffix)
  );
}

/** First "be + participle" in a sentence, or null. */
export function findPassive(sentence: string): string | null {
  for (const match of sentence.matchAll(RE_BE_PLUS_WORD)) {
    if (isParticiple(match[2])) {
      return `${match[0]}${match[1]}${match[2]}`;
    }
  }
  return null;
}

function continuesSentence(previous: string, next: string): boolean {
  if (RE_NEVER_ENDS_SENTENCE.test(previous)) {
    return true;
  }
  return (
    RE_REFERENCE_ABBREVIATION.test(previous) && RE_REFERENCE_NUMBER.test(next)
  );
}

/** Split into sentences, keeping "Fig. 1", "e.g." and "Dr." inside one sentence. */
export function splitSentences(text: string): string[] {
  const sentences: string[] = [];
  for (const chunk of text.split(RE_SENTENCE_SPLIT)) {
    const previous = sentences.at(-1);
    if (previous !== undefined && continuesSentence(previous, chunk)) {
      sentences[sentences.length - 1] = `${previous} ${chunk}`;
    } else {
      sentences.push(chunk);
    }
  }
  return sentences.map((s) => s.trim()).filter((s) => s.length > 0);
}

/* ------------------------------------------------------------------ */
/*  Passive voice analysis                                            */
/* ------------------------------------------------------------------ */

interface PassiveSentence {
  text: string;
  match: string;
}

interface AnalysisResult {
  totalSentences: number;
  passiveSentences: PassiveSentence[];
  passivePercent: number;
  wordCount: number;
}

export function analyzePassiveVoice(text: string): AnalysisResult | null {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return null;
  }

  const sentences = splitSentences(trimmed);

  const totalSentences = Math.max(sentences.length, 1);
  const passiveSentences: PassiveSentence[] = [];

  for (const sentence of sentences) {
    const match = findPassive(sentence);
    if (match) {
      passiveSentences.push({ text: sentence, match });
    }
  }

  const wordCount = trimmed.split(RE_SPLIT_WORDS).filter(Boolean).length;
  const passivePercent = (passiveSentences.length / totalSentences) * 100;

  return {
    totalSentences,
    passiveSentences,
    passivePercent,
    wordCount,
  };
}

/* ------------------------------------------------------------------ */
/*  Rating helper                                                     */
/* ------------------------------------------------------------------ */

interface RatingStrings {
  excellentLabel: string;
  excellentAdvice: string;
  goodLabel: string;
  goodAdvice: string;
  needsWorkLabel: string;
  needsWorkAdvice: string;
}

function getRating(
  percent: number,
  strings: RatingStrings
): {
  label: string;
  color: string;
  advice: string;
} {
  if (percent <= 10) {
    return {
      label: strings.excellentLabel,
      color:
        "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
      advice: strings.excellentAdvice,
    };
  }
  if (percent <= 20) {
    return {
      label: strings.goodLabel,
      color:
        "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
      advice: strings.goodAdvice,
    };
  }
  return {
    label: strings.needsWorkLabel,
    color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    advice: strings.needsWorkAdvice,
  };
}

/* ------------------------------------------------------------------ */
/*  Stat card                                                         */
/* ------------------------------------------------------------------ */

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const PASSIVE_VOICE_DETECTOR_STRINGS = {
  inputAriaLabel: "Text to detect passive voice in",
  inputPlaceholder: "Paste or type your text here to detect passive voice...",
  totalWords: "Total Words",
  totalSentences: "Total Sentences",
  passiveSentences: "Passive Sentences",
  passivePercent: "Passive %",
  rating: "Rating",
  excellentLabel: "Excellent",
  excellentAdvice: "Your writing uses active voice effectively. Great job!",
  goodLabel: "Good",
  goodAdvice:
    "Your passive voice usage is within acceptable range. Consider revising a few sentences for more impact.",
  needsWorkLabel: "Needs Work",
  needsWorkAdvice:
    "Your text has a high percentage of passive voice. Rewriting passive sentences in active voice will make your writing more direct and engaging.",
  passiveVoiceSentencesFound: "Passive Voice Sentences Found",
  passiveConstructionLabel: "Passive construction: ",
  noPassiveDetected: "No passive voice detected!",
  activeVoiceThroughout:
    "Your text uses active voice throughout. Great writing!",
} as const;

export default function PassiveVoiceDetectorWidget() {
  const [text, setText] = useState("");
  const strings = useToolUi(PASSIVE_VOICE_DETECTOR_STRINGS);
  const fmt = useToolNumber();

  const analysis = useMemo(() => analyzePassiveVoice(text), [text]);

  const rating = analysis
    ? getRating(analysis.passivePercent, {
        excellentLabel: strings.excellentLabel,
        excellentAdvice: strings.excellentAdvice,
        goodLabel: strings.goodLabel,
        goodAdvice: strings.goodAdvice,
        needsWorkLabel: strings.needsWorkLabel,
        needsWorkAdvice: strings.needsWorkAdvice,
      })
    : null;

  return (
    <>
      {/* Input */}
      <textarea
        aria-label={strings.inputAriaLabel}
        className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        onChange={(e) => setText(e.target.value)}
        placeholder={strings.inputPlaceholder}
        value={text}
      />

      {/* Stats */}
      {analysis && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard
            label={strings.totalWords}
            value={fmt(analysis.wordCount)}
          />
          <StatCard
            label={strings.totalSentences}
            value={fmt(analysis.totalSentences)}
          />
          <StatCard
            label={strings.passiveSentences}
            value={fmt(analysis.passiveSentences.length)}
          />
          <StatCard
            label={strings.passivePercent}
            value={`${fmt(analysis.passivePercent, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`}
          />
        </div>
      )}

      {/* Rating */}
      {analysis && rating && (
        <div className="rounded-lg border border-border bg-card p-5">
          <div className="mb-2 flex items-center gap-3">
            <h2 className="font-semibold text-foreground text-lg">
              {strings.rating}
            </h2>
            <span
              className={`rounded-full px-2.5 py-0.5 font-medium text-xs ${rating.color}`}
            >
              {rating.label}
            </span>
          </div>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {rating.advice}
          </p>
        </div>
      )}

      {/* Passive sentences list */}
      {analysis && analysis.passiveSentences.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.passiveVoiceSentencesFound}
          </h2>
          <div className="space-y-3">
            {analysis.passiveSentences.map((item, i) => (
              <div
                className="rounded-lg border border-border bg-card p-4"
                key={`passive-${i}-${item.match}`}
              >
                <p className="text-foreground text-sm leading-relaxed">
                  {item.text}
                </p>
                <p className="mt-2 text-muted-foreground text-xs">
                  {strings.passiveConstructionLabel}
                  <span className="font-medium text-red-600 dark:text-red-400">
                    {item.match}
                  </span>
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* No passive found */}
      {analysis && analysis.passiveSentences.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-5 text-center">
          <p className="font-medium text-foreground">
            {strings.noPassiveDetected}
          </p>
          <p className="mt-1 text-muted-foreground text-sm">
            {strings.activeVoiceThroughout}
          </p>
        </div>
      )}
    </>
  );
}
